import React from 'react';
import { Col, Form, Row } from 'antd';
import PropTypes from 'prop-types';

import BudgetTrackingCollapse from './BudgetTrackingCollapse';
import DisplayText from '../../../common/text/DisplayText';
import ProjectCurrencyInput from '../../ProjectCurrencyInput';
import ProjectNumberInput from './ProjectNumberInput';
import { currencyFormatter } from '../../../helpers/inputParsers';
import { ESTIMATED_LABEL_STYLE, ESTIMATED_DISPLAY_TEXT_STYLE } from '../budgetTrackingHelpers';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

const UNCHECKED_TEXT = 'Track contract values, monitor project profitability, compare estimated vs actual costs to improve future bids and identify cost overruns early and take corrective action';

/**
 * Shared estimates form content. State and derived values (contractTotalValue,
 * totalEstimatedAmount, expectedProfit, etc.) are owned by the parent.
 * Use inside a Form when using Form.Item names; optionally wrap in BudgetTrackingCollapse.
 */
export function EstimatesFormContent({
  useEstimates,
  isNotDisplay,
  useBuildEstimate,
  checkBoxes,
  contractTotalValue,
  contractValue,
  onContractValueChange,
  totalEstimatedAmount,
  expectedProfit,
  stateHours,
  onHoursChange,
  stateCosts,
  labourCost,
  onCostsChange,
  materialCostTotal,
  onMaterialCostChange,
  stateOverheadCost,
  estimatedOverheadCost,
  onOverheadCostChange,
  equipmentCostTotal,
  onEquipmentCostChange,
  stateBurdenRate,
  onBurdenRateChange,
  estimatedBurdenCost,
  subContractTotalValue,
  onSubContractTotalValueChange,
  customCategoryRows,
  allocatedHours,
  allocatedColor,
}) {
  if (!useEstimates) {
    return <span>{UNCHECKED_TEXT}</span>;
  }

  const labourCostDisplay = useBuildEstimate ? stateCosts : labourCost;

  return (
    <div>
      {checkBoxes}
      <div className="budget-tracking-estimate-overview">
        <Row gutter={20}>
          <Col span={6}>
            <Form.Item
              name="dummyContractValue"
              label="Current Contract Value"
              style={ESTIMATED_LABEL_STYLE}
            >
              <DisplayText
                title={contractTotalValue ? currencyFormatter(contractTotalValue) : 'N/A'}
                style={ESTIMATED_DISPLAY_TEXT_STYLE}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <ProjectCurrencyInput
              name="contractValue"
              title="Base Contract Value"
              isNotDisplay={isNotDisplay}
              onChange={onContractValueChange}
              value={contractValue}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
          <Col span={6}>
            <Form.Item
              name="Total Estimated Amount"
              label="Total Estimated Amount"
              style={ESTIMATED_LABEL_STYLE}
            >
              <DisplayText
                title={totalEstimatedAmount}
                style={ESTIMATED_DISPLAY_TEXT_STYLE}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="Expected Profit"
              label="Expected Profit"
              style={ESTIMATED_LABEL_STYLE}
            >
              <DisplayText
                title={expectedProfit}
                style={ESTIMATED_DISPLAY_TEXT_STYLE}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
      <div style={{ marginTop: 20 }}>
        <Row gutter={[20, 20]}>
          <Col span={8}>
            <ProjectNumberInput
              name="estimatedLabourHours"
              title="Estimated Labour Hours"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onHoursChange}
              value={stateHours}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              min={1}
              displayFormatter={(v) => `${v} hours`}
            />
          </Col>
          <Col span={8}>
            <ProjectCurrencyInput
              name="estimatedLabourCost"
              title="Estimated Labor Cost"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onCostsChange}
              value={labourCostDisplay}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
          <Col span={8}>
            <Form.Item
              name="averageLabourRate"
              label="Average Labour Rate"
              style={ESTIMATED_LABEL_STYLE}
            >
              <DisplayText
                title={stateCosts && stateHours ? `$ ${(stateCosts / stateHours).toFixed(2)}/hr` : 'N/A'}
                style={ESTIMATED_DISPLAY_TEXT_STYLE}
                displayAsDropdown
              />
            </Form.Item>
            <div className="budget-tracking-estimate-subtext">
              = Labor Cost ÷ Labour Hours
            </div>
          </Col>
        </Row>
        <Row gutter={[20, 20]}>
          <Col span={8}>
            <ProjectCurrencyInput
              name="estimatedMaterialCost"
              title="Estimated Material Cost"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onMaterialCostChange}
              value={materialCostTotal}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
          <Col span={8}>
            <ProjectCurrencyInput
              name="estimatedOverheadCost"
              title="Estimated Overhead Cost"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onOverheadCostChange}
              value={useBuildEstimate ? stateOverheadCost : estimatedOverheadCost}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
          <Col span={8}>
            <ProjectCurrencyInput
              name="estimatedEquipmentCost"
              title="Estimated Equipment Cost"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onEquipmentCostChange}
              value={equipmentCostTotal}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
        </Row>
        <Row gutter={[20, 20]}>
          <Col span={8}>
            <ProjectNumberInput
              name="estimatedBurdenRate"
              title="Burden Rate"
              isNotDisplay={isNotDisplay}
              onChange={onBurdenRateChange}
              value={stateBurdenRate}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              min={0}
              max={1}
              step={0.0001}
              precision={4}
              displayFormatter={(v) => v?.toFixed(4)}
            />
          </Col>
          <Col span={8}>
            <Form.Item
              name="estimatedBurdenCost"
              label="Estimated Burden Cost"
              style={ESTIMATED_LABEL_STYLE}
            >
              <DisplayText
                title={estimatedBurdenCost ? currencyFormatter(estimatedBurdenCost) : 'N/A'}
                style={ESTIMATED_DISPLAY_TEXT_STYLE}
                displayAsDropdown
              />
            </Form.Item>
            <div className="budget-tracking-estimate-subtext">
              = Labor Cost × Burden Rate
            </div>
          </Col>
          <Col span={8}>
            <ProjectCurrencyInput
              name="estimatedSubContractTotalValue"
              title="Total Sub-Contract Amount"
              isNotDisplay={isNotDisplay && !useBuildEstimate}
              onChange={onSubContractTotalValueChange}
              value={subContractTotalValue}
              labelStyle={ESTIMATED_LABEL_STYLE}
              displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
              displayAsDropdown
            />
          </Col>
        </Row>
        {customCategoryRows}
        {!!stateHours && (
          <Row gutter={[20, 20]}>
            <Col span={24}>
              <div className="budget-tracking-hours-allocated">
                <span style={ESTIMATED_LABEL_STYLE}>Hours Allocated:</span>
                <span style={{ ...ESTIMATED_LABEL_STYLE, color: allocatedColor }}>
                  {allocatedHours ?? 0}
                  /
                  {stateHours}
                </span>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}

EstimatesFormContent.propTypes = {
  useEstimates: PropTypes.bool.isRequired,
  isNotDisplay: PropTypes.bool,
  useBuildEstimate: PropTypes.bool,
  checkBoxes: PropTypes.node,
  contractTotalValue: PropTypes.number,
  contractValue: PropTypes.number,
  onContractValueChange: PropTypes.func,
  totalEstimatedAmount: PropTypes.string,
  expectedProfit: PropTypes.string,
  stateHours: PropTypes.number,
  onHoursChange: PropTypes.func,
  stateCosts: PropTypes.number,
  labourCost: PropTypes.number,
  onCostsChange: PropTypes.func,
  materialCostTotal: PropTypes.number,
  onMaterialCostChange: PropTypes.func,
  stateOverheadCost: PropTypes.number,
  estimatedOverheadCost: PropTypes.number,
  onOverheadCostChange: PropTypes.func,
  equipmentCostTotal: PropTypes.number,
  onEquipmentCostChange: PropTypes.func,
  stateBurdenRate: PropTypes.number,
  onBurdenRateChange: PropTypes.func,
  estimatedBurdenCost: PropTypes.number,
  subContractTotalValue: PropTypes.number,
  onSubContractTotalValueChange: PropTypes.func,
  customCategoryRows: PropTypes.node,
  allocatedHours: PropTypes.number,
  allocatedColor: PropTypes.string,
};

EstimatesFormContent.defaultProps = {
  isNotDisplay: false,
  useBuildEstimate: false,
  checkBoxes: null,
  contractTotalValue: null,
  contractValue: null,
  onContractValueChange: undefined,
  totalEstimatedAmount: '',
  expectedProfit: '',
  stateHours: null,
  onHoursChange: undefined,
  stateCosts: null,
  labourCost: null,
  onCostsChange: undefined,
  materialCostTotal: null,
  onMaterialCostChange: undefined,
  stateOverheadCost: null,
  estimatedOverheadCost: null,
  onOverheadCostChange: undefined,
  equipmentCostTotal: null,
  onEquipmentCostChange: undefined,
  stateBurdenRate: null,
  onBurdenRateChange: undefined,
  estimatedBurdenCost: null,
  subContractTotalValue: null,
  onSubContractTotalValueChange: undefined,
  customCategoryRows: null,
  allocatedHours: null,
  allocatedColor: undefined,
};

/**
 * Reusable estimates section: always uses BudgetTrackingCollapse.
 * Header: pass custom header (Create) or use headerTitle + optional Edit button (Cost Tracking).
 * Edit button in header switches to edit mode.
 */
export default function BudgetTrackingEstimates({
  header,
  showEditButton = false,
  wrapContentInForm = false,
  useEstimates,
  isNotDisplay,
  useBuildEstimate,
  checkBoxes,
  contractTotalValue,
  contractValue,
  onContractValueChange,
  totalEstimatedAmount,
  expectedProfit,
  stateHours,
  onHoursChange,
  stateCosts,
  labourCost,
  onCostsChange,
  materialCostTotal,
  onMaterialCostChange,
  stateOverheadCost,
  estimatedOverheadCost,
  onOverheadCostChange,
  equipmentCostTotal,
  onEquipmentCostChange,
  stateBurdenRate,
  onBurdenRateChange,
  estimatedBurdenCost,
  subContractTotalValue,
  onSubContractTotalValueChange,
  customCategoryRows,
  allocatedHours,
  allocatedColor,
  form,
  onSave,
  onEditClick,
}) {
  const defaultHeader = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <span className="budget-tracking-section-title">Estimates</span>
      {isNotDisplay && onSave ? (
        <OnTraccrButton
          title="Save"
          onClick={(e) => {
            e.stopPropagation();
            onSave();
          }}
          style={{ marginRight: 20 }}
        />
      ) : showEditButton && (
        <OnTraccrButton
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            if (onEditClick) onEditClick();
          }}
          style={{ marginRight: 20 }}
        />
      )}
    </div>
  );

  const content = (
    <EstimatesFormContent
      useEstimates={useEstimates}
      isNotDisplay={isNotDisplay}
      useBuildEstimate={useBuildEstimate}
      checkBoxes={checkBoxes}
      contractTotalValue={contractTotalValue}
      contractValue={contractValue}
      onContractValueChange={onContractValueChange}
      totalEstimatedAmount={totalEstimatedAmount}
      expectedProfit={expectedProfit}
      stateHours={stateHours}
      onHoursChange={onHoursChange}
      stateCosts={stateCosts}
      labourCost={labourCost}
      onCostsChange={onCostsChange}
      materialCostTotal={materialCostTotal}
      onMaterialCostChange={onMaterialCostChange}
      stateOverheadCost={stateOverheadCost}
      estimatedOverheadCost={estimatedOverheadCost}
      onOverheadCostChange={onOverheadCostChange}
      equipmentCostTotal={equipmentCostTotal}
      onEquipmentCostChange={onEquipmentCostChange}
      stateBurdenRate={stateBurdenRate}
      onBurdenRateChange={onBurdenRateChange}
      estimatedBurdenCost={estimatedBurdenCost}
      subContractTotalValue={subContractTotalValue}
      onSubContractTotalValueChange={onSubContractTotalValueChange}
      customCategoryRows={customCategoryRows}
      allocatedHours={allocatedHours}
      allocatedColor={allocatedColor}
    />
  );

  let formKey = isNotDisplay ? 'display' : 'edit';
  if (form) formKey = 'controlled';

  return (
    <BudgetTrackingCollapse
      panelKey="estimates"
      header={header ?? defaultHeader}
    >
      <div style={{ marginTop: 20 }}>
        {wrapContentInForm ? (
          <Form
            form={form}
            key={formKey}
            layout="vertical"
          >
            {content}
          </Form>
        ) : content}
      </div>
    </BudgetTrackingCollapse>
  );
}

/* eslint-disable react/forbid-prop-types */
BudgetTrackingEstimates.propTypes = {
  header: PropTypes.node,
  showEditButton: PropTypes.bool,
  wrapContentInForm: PropTypes.bool,
  form: PropTypes.object,
  onSave: PropTypes.func,
  onEditClick: PropTypes.func,
  ...EstimatesFormContent.propTypes,
};

BudgetTrackingEstimates.defaultProps = {
  header: null,
  showEditButton: false,
  wrapContentInForm: false,
  form: undefined,
  onSave: undefined,
  onEditClick: undefined,
  ...EstimatesFormContent.defaultProps,
};
